import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { type Brand, detectBrand } from '@/lib/brand'

interface BrandContextValue {
  brand: Brand
  refresh: () => void
}

const BrandContext = createContext<BrandContextValue | null>(null)

export function BrandProvider({ children }: { children: React.ReactNode }) {
  const [brand, setBrand] = useState<Brand>(() => detectBrand())

  // Stable identity: consumers put this in effect deps, and a new function each
  // render would re-run them. The compiler does not guarantee this one.
  const refresh = useCallback(() => setBrand(detectBrand()), [])

  // The provider sits ABOVE RouterProvider, so it cannot subscribe to router
  // state. `popstate` covers back/forward; anything that changes the brand by
  // navigating in-SPA must call `refresh()` itself.
  useEffect(() => {
    const onPopState = () => refresh()
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [refresh])

  // Mirror to <html data-brand> so CSS can override theme tokens per brand.
  useEffect(() => {
    document.documentElement.dataset.brand = brand.id
    return () => {
      delete document.documentElement.dataset.brand
    }
  }, [brand.id])

  return (
    <BrandContext.Provider value={{ brand, refresh }}>
      {children}
    </BrandContext.Provider>
  )
}

export function useBrand(): Brand {
  const value = useContext(BrandContext)
  if (!value) throw new Error('useBrand must be used within <BrandProvider>')
  return value.brand
}

/** Re-run brand detection — call after changing the URL in-SPA. */
export function useBrandRefresh(): () => void {
  const value = useContext(BrandContext)
  if (!value) {
    throw new Error('useBrandRefresh must be used within <BrandProvider>')
  }
  return value.refresh
}
